import React from 'react';
import { LegalPageLayout } from '@/components/shared/LegalPageLayout';

export default function SecurityPage() {
  return (
    <LegalPageLayout title="Security" lastUpdated="January 15, 2025">
      <div className="space-y-8">
        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">1. Our Security Commitment</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            At Agentick, security is not an afterthought — it is a foundational principle. We understand that businesses trust us with their customer conversations, business data, and operational processes. We take this trust seriously and implement enterprise-grade security measures to protect your information.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">2. Infrastructure Security</h2>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground font-body ml-2">
            <li><span className="text-foreground font-medium">Cloud Hosting</span> — Our infrastructure is hosted on enterprise-grade cloud providers with SOC 2 Type II and ISO 27001 certifications</li>
            <li><span className="text-foreground font-medium">Network Security</span> — Multi-layer firewall protection, DDoS mitigation, and network segmentation</li>
            <li><span className="text-foreground font-medium">Redundancy</span> — Geographic redundancy with automatic failover to ensure high availability</li>
            <li><span className="text-foreground font-medium">Monitoring</span> — 24/7 infrastructure monitoring with automated alerting for anomalies</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">3. Data Encryption</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed mb-3">
            All data is protected with industry-standard encryption:
          </p>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground font-body ml-2">
            <li><span className="text-foreground font-medium">In Transit</span> — TLS 1.3 encryption for all data transmitted between your devices and our servers</li>
            <li><span className="text-foreground font-medium">At Rest</span> — AES-256 encryption for all stored data, including knowledge bases and conversation logs</li>
            <li><span className="text-foreground font-medium">Key Management</span> — Cryptographic keys are stored in dedicated hardware security modules (HSMs)</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">4. Access Controls</h2>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground font-body ml-2">
            <li>Role-based access control (RBAC) with principle of least privilege</li>
            <li>Multi-factor authentication (MFA) for all administrative access</li>
            <li>Session management with automatic timeouts and token rotation</li>
            <li>Audit logging for all access to customer data and system configurations</li>
            <li>Regular access reviews and privilege de-escalation</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">5. Application Security</h2>
          <ul className="list-disc list-inside space-y-1.5 text-sm text-muted-foreground font-body ml-2">
            <li>Secure development lifecycle (SDLC) with security reviews at every stage</li>
            <li>Regular penetration testing by independent third-party security firms</li>
            <li>Automated vulnerability scanning in our CI/CD pipeline</li>
            <li>OWASP Top 10 compliance and mitigation</li>
            <li>Input validation, output encoding, and parameterized queries throughout</li>
          </ul>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">6. Data Privacy & Isolation</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            Each customer's data is logically isolated. Your knowledge base, conversation logs, and lead data are accessible only to your authorized team members. We maintain strict tenant isolation at the database and application layers, ensuring no cross-contamination between accounts.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">7. Incident Response</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            We maintain a comprehensive incident response plan that includes detection, containment, eradication, and recovery procedures. Our team conducts regular tabletop exercises and post-incident reviews. In the event of a security incident affecting your data, we will notify you promptly with full details and remediation steps.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-lg font-semibold text-foreground mb-3">8. Responsible Disclosure</h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            We welcome security researchers who help us keep Agentick secure. If you discover a vulnerability, please report it responsibly to <span className="text-primary">security@agentick.com</span>. We commit to acknowledging your report within 24 hours and providing regular updates until the issue is resolved.
          </p>
        </section>
      </div>
    </LegalPageLayout>
  );
}
